"use client"

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
  Box,
  Typography,
} from "@mui/material"
import { Close as CloseIcon, ArrowForward as ArrowForwardIcon } from "@mui/icons-material"
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from "@mui/lab"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import axios from "axios"
import { API_URL } from "../../constants"

const UplinkViewDialog = ({ open, onClose, user, onOpenUplinkEdit }) => {
  const { userInfo } = useSelector((state) => state.auth)
  const [parentChain, setParentChain] = useState([])
  const [loadingChain, setLoadingChain] = useState(false)

  useEffect(() => {
    const fetchParentChain = async () => {
      if (!user || !open) {
        setParentChain([])
        return
      }

      setLoadingChain(true)
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
        const { data } = await axios.get(`${API_URL}/api/users/${user._id}/parent-chain`, config)

        const processedChain = data

        // Prepend a static "System Admin" if the chain doesn't start with an admin
        if (processedChain.length === 0 || processedChain[0].role !== "admin") {
          processedChain.unshift({
            userId: "static-admin", // Unique ID for this static entry
            name: "System Admin",
            userName: "admin@platform.com", // Generic admin username
            role: "admin",
          })
        }

        setParentChain(processedChain)
      } catch (error) {
        console.error("Failed to fetch parent chain for view:", error)
        toast.error("Failed to load user hierarchy.")
        setParentChain([])
      } finally {
        setLoadingChain(false)
      }
    }

    fetchParentChain()
  }, [user, open, userInfo.token])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Uplink Hierarchy for: {user?.name} ({user?.role.toUpperCase()})
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loadingChain ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 150 }}>
            <CircularProgress />
          </Box>
        ) : parentChain.length > 0 ? (
          <Timeline position="alternate" sx={{ p: 0 }}>
            {" "}
            {/* Changed position to "alternate" and removed centering Box */}
            {parentChain.map((node, index) => (
              <TimelineItem key={node.userId}>
                <TimelineSeparator>
                  <TimelineDot
                    color={node.role === "admin" ? "primary" : node.role === "reseller" ? "secondary" : "grey"}
                  />
                  {index < parentChain.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ py: "12px", px: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {node.name} ({node.role.charAt(0).toUpperCase() + node.role.slice(1)})
                  </Typography>
                  {/* Conditionally render username for non-static admin */}
                  {node.userId !== "static-admin" && (
                    <Typography variant="body2" color="text.secondary">
                      Username: {node.userName}
                    </Typography>
                  )}
                  {/* {index < parentChain.length - 1 && (
                    <Box sx={{ display: "flex", alignItems: "center", mt: 0.5, color: "text.secondary" }}>
                      <ArrowForwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">is parent of</Typography>
                    </Box>
                  )} */}
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        ) : (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              No uplink hierarchy found for this user.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: "text.secondary" }}>
          Close
        </Button>
        {userInfo.user.role === "admin" && (
          <Button
            onClick={onOpenUplinkEdit}
            variant="contained"
            sx={{ bgcolor: "#008069", "&:hover": { bgcolor: "#128C7E" } }}
          >
            Change Uplink
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default UplinkViewDialog
